import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, CloudUpload, Delete, Image } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';

export const BackgroundBannerImage = ({
  bannerImage,
  setBannerImage,
  backgroundImage,
  setBackgroundImage,
}) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  // Extensiones permitidas
  const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

  const validateFileExtension = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return allowedExtensions.includes(fileExtension);
  };

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (validateFileExtension(file)) {
        setBannerImage(file);
        showSnackbar('Imagen banner cargada correctamente', 'success');
      } else {
        showSnackbar(
          `Formato no válido. Solo: ${allowedExtensions.join(', ')}`,
          'error'
        );
        e.target.value = '';
      }
    }
  };

  const handleBackgroundChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (validateFileExtension(file)) {
        setBackgroundImage(file);
        showSnackbar('Imagen background cargada correctamente', 'success');
      } else {
        showSnackbar(
          `Formato no válido. Solo: ${allowedExtensions.join(', ')}`,
          'error'
        );
        e.target.value = '';
      }
    }
  };

  const removeBannerImage = () => {
    setBannerImage(null);
    const input = document.getElementById('banner-upload');
    if (input) input.value = '';
    showSnackbar('Imagen banner eliminada', 'info');
  };

  const removeBackgroundImage = () => {
    setBackgroundImage(null);
    const input = document.getElementById('background-upload');
    if (input) input.value = '';
    showSnackbar('Imagen background eliminada', 'info');
  };

  // Función para determinar si es un archivo (File) o una URL (string)
  const isFile = (image) => image instanceof File;
  const isUrl = (image) => typeof image === 'string' && image.length > 0;

  // Previews mejorados - maneja tanto archivos como URLs
  const bannerPreview = useMemo(() => {
    if (!bannerImage) return '';
    if (isUrl(bannerImage)) return bannerImage; // Es una URL, la devolvemos tal como está
    if (isFile(bannerImage)) return URL.createObjectURL(bannerImage); // Es un File, creamos object URL
    return '';
  }, [bannerImage]);

  const backgroundPreview = useMemo(() => {
    if (!backgroundImage) return '';
    if (isUrl(backgroundImage)) return backgroundImage; // Es una URL, la devolvemos tal como está
    if (isFile(backgroundImage)) return URL.createObjectURL(backgroundImage); // Es un File, creamos object URL
    return '';
  }, [backgroundImage]);

  useEffect(() => {
    // Solo limpiar objectURLs cuando son archivos (File objects), no URLs
    return () => {
      if (bannerPreview && isFile(bannerImage)) {
        URL.revokeObjectURL(bannerPreview);
      }
      if (backgroundPreview && isFile(backgroundImage)) {
        URL.revokeObjectURL(backgroundPreview);
      }
    };
  }, [bannerPreview, backgroundPreview, bannerImage, backgroundImage]);

  // Estilos de caja grande y con proporción
  const baseBoxStyle = {
    width: '100%',
    aspectRatio: '4 / 3',
    minHeight: 160,
    border: '2px dashed #ccc',
    borderRadius: 2,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    '&:hover': { borderColor: '#1976d2', backgroundColor: '#f7f7f7' },
  };

  const successStyle = {
    ...baseBoxStyle,
    borderColor: '#4caf50',
    backgroundColor: '#f1f8e9',
  };

  const UploadEmptyState = ({ text }) => (
    <Box sx={{ textAlign: 'center' }}>
      <CloudUpload sx={{ fontSize: 36, mb: 1, color: 'text.secondary' }} />
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );

  const ImagePreview = ({ src, label, onRemove, isExistingImage }) => (
    <>
      <img
        src={src}
        alt={label}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 8,
          bottom: 8,
          px: 1,
          py: 0.25,
          bgcolor: 'rgba(0,0,0,0.55)',
          color: 'white',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <CheckCircle fontSize="small" />
        <Typography variant="caption" sx={{ lineHeight: 1 }}>
          {isExistingImage ? 'Imagen existente' : label || 'Imagen cargada'}
        </Typography>
      </Box>
    </>
  );

  // Función para obtener el nombre del archivo o etiqueta
  const getBannerLabel = () => {
    if (isFile(bannerImage)) return bannerImage.name;
    if (isUrl(bannerImage)) return 'Banner existente';
    return 'Banner';
  };

  const getBackgroundLabel = () => {
    if (isFile(backgroundImage)) return backgroundImage.name;
    if (isUrl(backgroundImage)) return 'Background existente';
    return 'Background';
  };

  return (
    <>
      <Typography
        variant="h6"
        style={{ fontWeight: 'bold', marginBottom: '15px' }}
      >
        Background y banner de la encuesta:
      </Typography>
      <Box sx={{ maxWidth: 700 }}>
        <Card elevation={0}>
          <CardContent>
            <Box sx={{ mb: 2, p: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Formatos permitidos:</strong>{' '}
                {allowedExtensions.join(', ').toUpperCase()}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Banner - lado izquierdo en md+ */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Image color="primary" /> Imagen Banner
                </Typography>

                <input
                  accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
                  style={{ display: 'none' }}
                  id="banner-upload"
                  type="file"
                  onChange={handleBannerChange}
                />
                <label htmlFor="banner-upload" style={{ display: 'block' }}>
                  <Box sx={bannerImage ? successStyle : baseBoxStyle}>
                    {bannerPreview ? (
                      <ImagePreview
                        src={bannerPreview}
                        label={getBannerLabel()}
                        onRemove={removeBannerImage}
                        isExistingImage={isUrl(bannerImage)}
                      />
                    ) : (
                      <UploadEmptyState text="Haz clic para subir banner" />
                    )}
                  </Box>
                </label>
              </Grid>

              {/* Background - lado derecho en md+ */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Image color="primary" /> Imagen Background
                </Typography>

                <input
                  accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
                  style={{ display: 'none' }}
                  id="background-upload"
                  type="file"
                  onChange={handleBackgroundChange}
                />
                <label htmlFor="background-upload" style={{ display: 'block' }}>
                  <Box sx={backgroundImage ? successStyle : baseBoxStyle}>
                    {backgroundPreview ? (
                      <ImagePreview
                        src={backgroundPreview}
                        label={getBackgroundLabel()}
                        onRemove={removeBackgroundImage}
                        isExistingImage={isUrl(backgroundImage)}
                      />
                    ) : (
                      <UploadEmptyState text="Haz clic para subir background" />
                    )}
                  </Box>
                </label>
              </Grid>
              {/* Vista previa compacta debajo (opcional) */}
              {(bannerImage || backgroundImage) && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0', width:'100%' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CheckCircle color="success" fontSize="small" sx={{marginLeft : '10px'}}/> Archivos
                    seleccionados
                  </Typography>

                  <Stack spacing={1}>
                    {bannerImage && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <Image color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Banner
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {bannerImage.name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={removeBannerImage}
                          sx={{ ml: 'auto' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {backgroundImage && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <Image color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Background
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {backgroundImage.name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={removeBackgroundImage}
                          sx={{ ml: 'auto' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
